/* eslint react/no-multi-comp: 0 */
import React, {
    useState,
    useEffect,
    useMemo,
    useContext,
    useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { UnorderedListOutlined, HistoryOutlined } from '@ant-design/icons';
import connect from '@app/utils/connect';
import { Formik, useFormikContext } from 'formik';
import combineActions from '@app/utils/combineActions';
import useFetchReports from '@app/components/hooks/reports/useFetchReports';
import useArchiveReport from '@app/components/hooks/reports/useArchiveReport';
import { useHandleSubmit } from '@app/components/hooks/reports/useHandleSubmit';
import { useReportPolling } from '@app/components/hooks/reports/useReportPolling';
import useAddReport from '@app/components/hooks/reports/useAddReport';
import fetchScheduledReports from '@app/actions/reports/fetchScheduledReports';
import sendNotification from '@app/actions/notifications/send';
import clearAllNotifs from '@app/actions/notifications/clearAll';
import removeScheduledReport from '@app/actions/reports/removeScheduledReport';
import addScheduledReport from '@app/actions/reports/addScheduledReport';
import { getHistoryColumns } from './tableHeaders/buildHistoryHeaders';
import { getScheduleColumns } from './tableHeaders/buildScheduleHeaders';
import {
    EDGE_RIGHTSHOLDER_ATTRIBUTION,
    PREMIUM_REPORTS,
} from '@app/constants/config';
import ResizableTable from '@app/components/common/Table';
import sportsApi from '@app/utils/sportsApi';
import * as fns from 'date-fns';
import { Flex, Tabs, Typography, ConfigProvider } from '@app/ui';
const { Title } = Typography;
import { initialValues } from './initialValues';
import addReportDownloading from '@app/actions/reports/add';
import updateReportDownloading from '@app/actions/reports/update';
import dayjs from 'dayjs';

import './Reports.scss';
import { DeleteReportModal } from './DeleteReportModal';
import ReportsConfigContainer from './ReportsConfigContainer';
import ReportsConfigFilters from './ReportsConfigFilters';

import { Client, User } from '@app/types/api';
import Provider, { ReportsContext } from './ReportsContext';
import ScheduleModalV2 from './ScheduleModal';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';

type ReportsV2Props = {
    client: Client;
    user: User;
    filters: any;
    fetchScheduledReports: () => void;
    removeScheduledReport: (id: string) => void;
    addScheduledReport: (schedule: any) => void;
    scheduledReports: any[];
    sendNotification: any;
    clearAllNotifs: () => void;
    updateReportDownloading: (data: any) => void;
    addReportDownloading: (data: any) => void;
};

export const RepopulateHandler: React.FC<{
    pptReporting: boolean;
    sendNotification: any;
    t: (key: string) => string;
}> = ({ pptReporting, sendNotification, t }) => {
    const { setFieldValue, resetForm, values } = useFormikContext<any>();
    const {
        rhTreeData,
        sponsorOptions,
        placementOptions,
        hasReportsBroadcastCountryFilter,
    } = useContext(ReportsContext);
    const [pendingSponsorValidation, setPendingSponsorValidation] =
        React.useState<{
            sponsorIds: string[];
        } | null>(null);
    const [pendingPlacementValidation, setPendingPlacementValidation] =
        React.useState<{
            placementId: string;
        } | null>(null);
    const [hasFilteredValues, setHasFilteredValues] = React.useState(false);

    // Handle sponsor validation after rightsholders are set
    React.useEffect(() => {
        if (
            pendingSponsorValidation &&
            values.rightsholders &&
            values.rightsholders.length > 0
        ) {
            const { sponsorIds } = pendingSponsorValidation;

            // build an array of available sponsors consisting of the values of the elements in sponsorOptions
            const availableSponsors = sponsorOptions.map(
                sponsor => sponsor.value,
            );

            // filter the sponsorIds to only include the ones that are in the availableSponsors
            const validSponsors = sponsorIds.filter(id =>
                availableSponsors.includes(id),
            );

            // Check if any sponsors were filtered out
            if (validSponsors.length < sponsorIds.length) {
                setHasFilteredValues(true);
            }

            setFieldValue('sponsors', validSponsors, false);
            setPendingSponsorValidation(null);
        }
    }, [
        pendingSponsorValidation,
        values.rightsholders,
        sponsorOptions,
        setFieldValue,
    ]);

    // Handle placement validation after rightsholders are set
    React.useEffect(() => {
        if (
            pendingPlacementValidation &&
            values.rightsholders &&
            values.rightsholders.length === 1
        ) {
            const { placementId } = pendingPlacementValidation;

            // build an array of available placements consisting of the values of the elements in placementOptions
            const availablePlacements = placementOptions.map(
                placement => placement.value,
            );

            // filter the placementId to only include the ones that are in the availablePlacements
            const validPlacement = availablePlacements.includes(placementId)
                ? placementId
                : null;

            // Check if placement was filtered out
            if (!validPlacement && placementId) {
                setHasFilteredValues(true);
            }

            setFieldValue('placement', validPlacement, false);
            setPendingPlacementValidation(null);
        }
    }, [
        pendingPlacementValidation,
        values.rightsholders,
        placementOptions,
        setFieldValue,
    ]);

    // Show toast message when values have been filtered
    React.useEffect(() => {
        if (hasFilteredValues) {
            sendNotification(
                t('reports.messages.warning.inputValuesUnavailable'),
                { context: 'warning' },
            );
            setHasFilteredValues(false);
        }
    }, [hasFilteredValues, sendNotification, t]);

    const handleRepopulate = (row: any) => {
        // Reset filtered values state for new repopulation
        setHasFilteredValues(false);

        const isPacingReport = !!row.goal;
        resetForm({
            values: {
                ...initialValues,
                exportAs: pptReporting ? 'powerpoint' : 'pdf',
            },
        });

        setFieldValue(
            'reportType',
            isPacingReport ? 'pacing' : 'standard',
            false,
        );

        if (isPacingReport) {
            setFieldValue('goal', row.goal, false);
            setFieldValue('goalName', row.goalName, false);
        } else {
            if (row.startDate || row.endDate) {
                setFieldValue('dateSelectionType', 'specific', false);
                if (row.startDate) {
                    setFieldValue('startDate', dayjs(row.startDate), false);
                }
                if (row.endDate) {
                    setFieldValue('endDate', dayjs(row.endDate), false);
                }
            } else {
                setFieldValue('dateSelectionType', 'all', false);
            }

            if (row.rightsholder) {
                const rightsholderIds = Array.isArray(row.rightsholder)
                    ? row.rightsholder
                    : row.rightsholder.split(',').map(id => id.trim());

                // build an array of available rightsholders consisting of the values of the children of every element in rhTreeData
                const availableRightsholders = rhTreeData.flatMap(rh =>
                    rh.children.map(child => child.value),
                );

                // filter the rightsholderIds to only include the ones that are in the availableRightsholders
                const validRightsholders = rightsholderIds.filter(id =>
                    availableRightsholders.includes(id),
                );

                // Check if any rightsholders were filtered out
                if (validRightsholders.length < rightsholderIds.length) {
                    setHasFilteredValues(true);
                }

                setFieldValue(
                    'selectedTreeRightsholders',
                    validRightsholders,
                    false,
                );
                setFieldValue('rightsholders', validRightsholders, false);
            }

            if (row.sponsor) {
                const sponsorIds = Array.isArray(row.sponsor)
                    ? row.sponsor
                    : [row.sponsor];

                // If we have rightsholders to set, defer sponsor validation until after rightsholders are set
                if (row.rightsholder) {
                    setPendingSponsorValidation({ sponsorIds });
                } else {
                    // build an array of available sponsors consisting of the values of the elements in sponsorOptions
                    const availableSponsors = sponsorOptions.map(
                        sponsor => sponsor.value,
                    );

                    // filter the sponsorIds to only include the ones that are in the availableSponsors
                    const validSponsors = sponsorIds.filter(id =>
                        availableSponsors.includes(id),
                    );

                    // Check if any sponsors were filtered out
                    if (validSponsors.length < sponsorIds.length) {
                        setHasFilteredValues(true);
                    }

                    setFieldValue('sponsors', validSponsors, false);
                }
            }

            if (row.placement && row.placement !== 'false') {
                setFieldValue('placementSelectionType', 'single', false);

                // If we have rightsholders to set, defer placement validation until after rightsholders are set
                if (row.rightsholder) {
                    setPendingPlacementValidation({
                        placementId: row.placement,
                    });
                } else {
                    // build an array of available placements consisting of the values of the elements in placementOptions
                    const availablePlacements = placementOptions.map(
                        placement => placement.value,
                    );

                    // filter the placementId to only include the ones that are in the availablePlacements
                    const validPlacement = availablePlacements.includes(
                        row.placement,
                    )
                        ? row.placement
                        : null;

                    // Check if placement was filtered out
                    if (!validPlacement && row.placement) {
                        setHasFilteredValues(true);
                    }

                    setFieldValue('placement', validPlacement, false);
                }
            } else {
                setFieldValue('placementSelectionType', 'all', false);
                setFieldValue('placement', null, false);
            }
        }

        if (row.includesMethodologySlides !== undefined) {
            setFieldValue(
                'includesMethodologySlides',
                row.includesMethodologySlides,
                false,
            );
        }

        // Only restore a saved country restriction while the client can still
        // see and clear the selector. Without the permission the resetForm
        // above already left the form unrestricted, so skipping these writes
        // is the correct end state.
        if (hasReportsBroadcastCountryFilter) {
            if (Array.isArray(row.broadcastCountryIds)) {
                setFieldValue(
                    'broadcastCountryIds',
                    row.broadcastCountryIds,
                    false,
                );
            }

            if (Array.isArray(row.broadcastCountryNames)) {
                setFieldValue(
                    'broadcastCountryNames',
                    row.broadcastCountryNames,
                    false,
                );
            }
        }

        if (row.exportAs) {
            setFieldValue('exportAs', row.exportAs.toLowerCase(), false);
        }
    };

    React.useEffect(() => {
        (window as any).handleRepopulate = handleRepopulate;
    }, [
        rhTreeData,
        sponsorOptions,
        placementOptions,
        hasReportsBroadcastCountryFilter,
    ]);

    return null;
};

const ReportsV2: React.FC<ReportsV2Props> = ({
    client,
    user,
    filters,
    fetchScheduledReports,
    removeScheduledReport,
    addScheduledReport,
    scheduledReports,
    sendNotification,
    clearAllNotifs,
    updateReportDownloading,
    addReportDownloading,
}) => {
    const { t } = useTranslation('tools');

    const REPORTS_TABS = useMemo(
        () => [
            {
                key: 'history',
                title: t('reports.tabs.history'),
                icon: <UnorderedListOutlined />,
            },
            {
                key: 'schedules',
                title: t('reports.tabs.schedules'),
                icon: <HistoryOutlined />,
            },
        ],
        [t],
    );

    // Memoize the sendNotification function to prevent infinite re-renders
    const memoizedSendNotification = useCallback(sendNotification, [
        sendNotification,
    ]);
    const memoizedClearAllNotifs = useCallback(clearAllNotifs, [
        clearAllNotifs,
    ]);
    const memoizedUpdateReportDownloading = useCallback(
        updateReportDownloading,
        [updateReportDownloading],
    );

    const pptReporting = client.permissions.includes(PREMIUM_REPORTS);
    const [activeKey, setActiveKey] = useState('history');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [deleteType, setDeleteType] = useState<'report' | 'schedule'>(
        'report',
    );
    const [isDeleting, setIsDeleting] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editScheduleData, setEditScheduleData] = useState<any>(null);
    const [isCreateMode, setIsCreateMode] = useState(false);

    const [reportDownloadState, setReportDownloadState] = useState<{
        loadingReportId?: string;
        reportDownloadingId?: string;
    }>({});

    const { rightsholderAttribution } = useRightsholderAttribution({ client });

    const hasRhAttributionPermission = client.permissions?.includes(
        EDGE_RIGHTSHOLDER_ATTRIBUTION,
    );

    const showDrawer = () => setIsDrawerOpen(true);
    const closeDrawer = () => setIsDrawerOpen(false);

    const openScheduleModalForCreate = () => {
        setIsCreateMode(true);
        setEditMode(false);
        setEditScheduleData(null);
        setIsScheduleModalOpen(true);
    };

    const closeScheduleModal = () => {
        setIsScheduleModalOpen(false);
        setEditMode(false);
        setEditScheduleData(null);
        setIsCreateMode(false);
    };

    const { mutate: addReport } = useAddReport({
        pptReporting,
        clientId: client.id,
    });

    const {
        data: history,
        isLoading: isLoadingHistory,
        refetch: refetchReports,
    } = useFetchReports({
        pptReporting,
        clientId: client.id,
        limit: 2000,
    });

    const showMethodologyColumn = useMemo(
        () =>
            hasRhAttributionPermission ||
            (history ?? []).some(
                report => report.useRightsholderAttribution === true,
            ),
        [hasRhAttributionPermission, history],
    );

    useReportPolling({
        reportDownloadState,
        setReportDownloadState,
        sendNotification: memoizedSendNotification,
        clearAllNotifs: memoizedClearAllNotifs,
        addReport,
        updateReportDownloading: memoizedUpdateReportDownloading,
        refetchReports,
    });

    const { mutate: archiveReport } = useArchiveReport({
        pptReporting,
        clientId: client.id,
    });

    const sortedScheduledReports = useMemo(
        () =>
            scheduledReports.sort((a, b) => {
                const aCreatedAt = fns.parseISO(a.createdAt).getTime();
                const bCreatedAt = fns.parseISO(b.createdAt).getTime();
                return bCreatedAt - aCreatedAt;
            }),
        [scheduledReports],
    );

    useEffect(() => {
        fetchScheduledReports();
    }, []);

    const handleDownload = async (row: any) => {
        try {
            const response = await fetch(row.reportUrl);
            const blob = await response.blob();
            const ext = row.exportAs?.toLowerCase() === 'pdf' ? 'pdf' : 'pptx';
            const a = document.createElement('a');
            a.href = window.URL.createObjectURL(blob);
            a.download = `${row.name || 'report'}.${ext}`;
            a.click();
        } catch (err) {
            console.error('Error downloading report:', err);
        }
    };

    const handleDelete = (row: any) => {
        setSelectedRow(row);
        setDeleteType('report');
        setShowDeleteModal(true);
    };

    const handleDeleteSchedule = (row: any) => {
        setSelectedRow(row);
        setDeleteType('schedule');
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (selectedRow) {
            setIsDeleting(true);
            try {
                if (deleteType === 'report') {
                    await archiveReport(selectedRow.id);
                    await refetchReports();
                    memoizedSendNotification(
                        t('reports.messages.success.reportDeleted'),
                        {
                            context: 'success',
                        },
                    );
                } else if (deleteType === 'schedule') {
                    await sportsApi
                        .url(`/mvc-schedules/${selectedRow.scheduleId}`)
                        .delete();
                    removeScheduledReport(selectedRow.scheduleId);
                    memoizedSendNotification(
                        t('reports.messages.success.scheduleDeleted'),
                        {
                            context: 'success',
                        },
                    );
                }
            } catch (err) {
                console.error('Delete failed:', err);
            } finally {
                setIsDeleting(false);
                setShowDeleteModal(false);
            }
        }
    };

    const handleEditSchedule = (row: any) => {
        setEditMode(true);
        setEditScheduleData(row);
        setIsScheduleModalOpen(true);
    };

    const scheduleColumns = getScheduleColumns({
        onEdit: handleEditSchedule,
        onDelete: handleDeleteSchedule,
        currentUserEmail: user?.userEmail || '',
        isGumgumUser: user?.gumgumUser || false,
        t,
    });

    const historyColumns = getHistoryColumns({
        onRepopulate: (row: any) => {
            if ((window as any).handleRepopulate) {
                (window as any).handleRepopulate(row);
            }
        },
        onDownload: handleDownload,
        onDelete: handleDelete,
        showMethodologyColumn,
        t,
    });

    const isHistory = activeKey === 'history';

    const dataSource = isHistory ? history : sortedScheduledReports;
    const columns = isHistory ? historyColumns : scheduleColumns;
    const loading = isHistory ? isLoadingHistory : false;

    const tabItems = REPORTS_TABS.map(({ key, title, icon }) => ({
        key,
        label: (
            <Flex align="center" gap="small">
                {icon}
                {title}
            </Flex>
        ),
        children: (
            <div className="reports-table-wrapper">
                <Title level={4} style={{ margin: 0 }}>
                    {isHistory
                        ? t('reports.tables.history.title')
                        : t('reports.tables.schedules.title')}
                </Title>
                <ConfigProvider
                    theme={{
                        components: {
                            Dropdown: {
                                controlItemBgHover: '#E6F4FF',
                            },
                        },
                    }}
                >
                    <div
                        style={{
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            width: '100%',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <ResizableTable
                            className="reports-table"
                            columns={columns}
                            dataSource={dataSource}
                            loading={loading}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                showSizeChanger: false,
                                onChange: (page, pageSize) => {
                                    setPagination({
                                        current: page,
                                        pageSize,
                                    });
                                },
                            }}
                            rowKey={record => record.id || record.key}
                            locale={{
                                emptyText: isHistory
                                    ? t('reports.tables.history.empty')
                                    : t('reports.tables.schedules.empty'),
                            }}
                            bordered
                            rowClassName={(_, index) => {
                                const startIndex =
                                    (pagination.current - 1) *
                                    pagination.pageSize;
                                const endIndex =
                                    startIndex + pagination.pageSize;
                                const visibleRows = dataSource.slice(
                                    startIndex,
                                    endIndex,
                                );
                                const isLastVisible =
                                    index === visibleRows.length - 1;

                                return `${index % 2 === 0 ? 'table-row-light' : 'table-row-dark'} ${
                                    isLastVisible ? 'last-row' : ''
                                }`;
                            }}
                        />
                    </div>
                </ConfigProvider>
            </div>
        ),
    }));

    const initialFilters = {
        ...initialValues,
        exportAs: pptReporting ? 'powerpoint' : 'pdf',
    };

    const handleSubmit = useHandleSubmit({
        user,
        filters,
        client,
        closeDrawer,
        sendNotification,
        addReportDownloading,
        updateReportDownloading,
        addReport,
        setReportDownloadState,
        rightsholderAttribution,
    });

    const scheduleHookConfig = {
        user,
        client,
        filters,
        sendNotification,
        addScheduledReport,
        setActiveTable: setActiveKey,
        editScheduleId:
            editMode && editScheduleData ? editScheduleData.scheduleId : null,
        editScheduleData: editMode ? editScheduleData : null,
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialFilters}
                initialStatus={{
                    isSubmitting: false,
                }}
                onSubmit={handleSubmit}
            >
                <>
                    <div
                        className="reports-v2-container"
                        style={{ padding: 24 }}
                    >
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                color: '#1f1f1f',
                                fontWeight: 400,
                                marginBottom: 24,
                                paddingLeft: 12,
                                fontSize: 32,
                            }}
                        >
                            {t('reports.pageTitle')}
                        </Title>
                        <ReportsConfigContainer>
                            <Provider
                                closeDrawer={closeDrawer}
                                showDrawer={showDrawer}
                                isDrawerOpen={isDrawerOpen}
                                addScheduledReport={addScheduledReport}
                                setActiveTable={setActiveKey}
                                user={user}
                                scheduleHookConfig={scheduleHookConfig}
                                openScheduleModalForCreate={
                                    openScheduleModalForCreate
                                }
                                closeScheduleModal={closeScheduleModal}
                                isCreateMode={isCreateMode}
                            >
                                <ReportsConfigFilters />
                                <RepopulateHandler
                                    pptReporting={pptReporting}
                                    sendNotification={sendNotification}
                                    t={t}
                                />
                            </Provider>
                        </ReportsConfigContainer>

                        <ConfigProvider
                            theme={{
                                components: {
                                    Tabs: {
                                        titleFontSize: 14,
                                    },
                                },
                            }}
                        >
                            <Tabs
                                className="reports-tabs"
                                activeKey={activeKey}
                                onChange={setActiveKey}
                                items={tabItems}
                            />
                        </ConfigProvider>
                    </div>
                    <DeleteReportModal
                        visible={showDeleteModal}
                        onCancel={() => setShowDeleteModal(false)}
                        onConfirm={confirmDelete}
                        entity={
                            deleteType === 'report'
                                ? 'Report'
                                : 'Scheduled Report'
                        }
                        loading={isDeleting}
                    />
                    <ScheduleModalV2
                        open={isScheduleModalOpen}
                        onCancel={closeScheduleModal}
                        scheduleHookConfig={scheduleHookConfig}
                        editMode={editMode}
                        editScheduleData={editScheduleData}
                        rightsholderAttribution={rightsholderAttribution}
                    />
                </>
            </Formik>
        </>
    );
};

export default connect(
    ['filters', 'scheduledReports', 'client', 'user'],
    combineActions({
        addReportDownloading,
        fetchScheduledReports,
        removeScheduledReport,
        addScheduledReport,
        sendNotification,
        clearAllNotifs,
        updateReportDownloading,
    }),
)(ReportsV2);
